import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useAccessData } from '../hooks/useAccessData.js'
import LoadingSpinner from '../components/LoadingSpinner.jsx'

/**
 * DeviceAccessRequestsPage - Device access requests landing page for email links
 * 
 * URL: /devices/:deviceId/access-requests
 * Purpose: Direct access to pending access requests for a device (for device admins)
 * Used in: Access request notification emails, new access request emails
 */
function DeviceAccessRequestsPage() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { user, selectedDeviceId, selectDevice } = useCloudContext()
  const { useUserDevices } = useAccessData()
  
  const [state, setState] = useState({
    loading: true,
    error: null,
    device: null,
    redirecting: false,
  })

  const { data: userDevices, isLoading: devicesLoading } = useUserDevices()

  // Load device and access requests
  useEffect(() => {
    if (!deviceId || !user || devicesLoading) return

    const loadDeviceAccessRequests = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }))
        
        // Check if user has access to this device
        const userDevice = userDevices?.find(device => device.id === deviceId)
        
        if (!userDevice) {
          // Device exists but user doesn't have access
          setState(prev => ({ 
            ...prev, 
            loading: false, 
            device: { id: deviceId, name: deviceId },
            error: 'You do not have access to this device.',
          }))
          return
        }
        
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          device: userDevice,
        }))
        
        // Auto-select the device
        if (selectedDeviceId !== deviceId) {
          selectDevice(deviceId)
        }
        
        // Redirect to access management page
        setState(prev => ({ ...prev, redirecting: true }))
        setTimeout(() => {
          navigate('/access-management', { 
            replace: true,
            state: { 
              highlightDeviceId: deviceId,
            },
          })
        }, 1500)
        
      } catch (error) {
        console.error('Error loading device access requests:', error)
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Failed to load access requests. Please try again.',
        }))
      }
    }

    loadDeviceAccessRequests()
  }, [deviceId, user, userDevices, devicesLoading, selectedDeviceId, selectDevice, navigate])

  const handleGoToConfiguration = useCallback(() => {
    navigate('/access-management', { 
      replace: true,
    })
  }, [navigate])

  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  // Loading state
  if (state.loading || devicesLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="mt-3">
            <h5 className="mb-1">Loading Access Requests...</h5>
            <p className="text-muted">Checking device permissions</p>
          </div>
        </div>
      </div>
    )
  }

  // Redirecting state (success)
  if (state.redirecting && !state.error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="bi bi-gear text-primary" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">Accessing Device Management</h4>
                  <p className="text-muted mb-4">
                    Redirecting to access management for {state.device?.name || deviceId}...
                  </p>
                  
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Redirecting...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (state.error) {
    const isAccessDenied = state.error.includes('do not have access') || state.error.includes('do not have permission')
    const isNotFound = state.error.includes('not found')
    
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className={`bi ${isNotFound ? 'bi-exclamation-triangle' : 'bi-lock'} ${isNotFound ? 'text-danger' : 'text-warning'}`} style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="mb-3">
                    {isNotFound ? 'Device Not Found' : isAccessDenied ? 'Access Required' : 'Request Problem'}
                  </h4>
                  <p className="text-muted mb-4">{state.error}</p>
                  
                  {/* Device Info (if available) */}
                  {state.device && (
                    <div className="bg-light rounded p-3 mb-4">
                      <div className="fw-semibold">{state.device.name || state.device.id}</div>
                      <small className="text-muted">Device ID: {state.device.id}</small>
                    </div>
                  )}
                  
                  <div className="d-grid gap-2">
                    {!isNotFound && (
                      <button 
                        className="btn btn-primary"
                        onClick={handleGoToConfiguration}
                      >
                        <i className="bi bi-gear me-2"></i>
                        Go to Access Management
                      </button>
                    )}
                    
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={handleGoToDashboard}
                    >
                      <i className="bi bi-house me-2"></i>
                      Go to Dashboard
                    </button>
                    
                    {!isNotFound && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => window.location.reload()}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                      </button>
                    )}
                  </div>
                  
                  {isAccessDenied && (
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Only device administrators can manage access requests. 
                        <a href="mailto:support@revatek.com" className="text-decoration-none ms-1">Contact support</a> if you need help.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // This shouldn't normally be reached
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="text-center">
        <h5>Unexpected state</h5>
        <button className="btn btn-primary mt-3" onClick={handleGoToDashboard}>
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

export default DeviceAccessRequestsPage
